/**
 * Auth0 configuration helpers
 * Provides environment-aware configuration based on Architeia infrastructure
 */
import { getRuntimeConfig } from '@architeia/config';
import { getEnvironment, getAuth0Audience } from './urls.js';
export { getKoraApiUrl } from './urls.js';
function isAuth0DebugEnabled() {
    return import.meta.env?.VITE_AUTH0_DEBUG === 'true';
}
/**
 * Get Auth0 configuration for the current environment
 *
 * @example
 * ```ts
 * const config = getAuth0Config({
 *   domain: 'login.example.com',
 *   clientId: 'spa-client-id',
 * })
 * ```
 */
export function getAuth0Config(options) {
    const env = getEnvironment();
    return {
        domain: options.domain,
        clientId: options.clientId,
        audience: options.audience || getAuth0Audience(env),
        redirectUri: options.redirectUri,
        logoutRedirectUri: options.logoutRedirectUri,
        scope: options.scope,
        cacheLocation: options.cacheLocation,
        useRefreshTokens: options.useRefreshTokens
    };
}
/**
 * Get default Auth0 configuration from the runtime configuration (AINFRA-171, `/config.js`)
 *
 * Reads:
 * - auth0Domain (VITE_AUTH0_DOMAIN on the container)
 * - auth0ClientId (VITE_AUTH0_CLIENT_ID)
 * - auth0Audience (VITE_AUTH0_AUDIENCE, optional, will use environment-based default)
 *
 * @example
 * ```ts
 * const config = getDefaultAuth0Config()
 * const { isAuthenticated, user } = useAuth0(config)
 * ```
 */
export function getDefaultAuth0Config() {
    const runtime = getRuntimeConfig();
    const domain = runtime.auth0Domain;
    const clientId = runtime.auth0ClientId;
    if (!domain || !clientId) {
        throw new Error('Missing required Auth0 environment variables. ' +
            'Please set VITE_AUTH0_DOMAIN and VITE_AUTH0_CLIENT_ID');
    }
    const redirectUri = window.location.origin + '/callback';
    const logoutRedirectUri = window.location.origin;
    if (isAuth0DebugEnabled()) {
        // eslint-disable-next-line no-console
        console.log('[Auth0 Config]', {
            domain,
            clientId: clientId.substring(0, 10) + '...', // Only show first 10 chars for security
            redirectUri,
            logoutRedirectUri,
            origin: window.location.origin,
            fullUrl: window.location.href
        });
    }
    return getAuth0Config({
        domain,
        clientId,
        audience: runtime.auth0Audience,
        redirectUri,
        logoutRedirectUri
    });
}
